const {triageEnum} = require("./triage");
const severities = require('../all/severity.js');

const ACTIONS = {
  DISPLAY_HIDDEN: 'display_hidden',
  NOT_ASSESSED: 'not_assessed'
};

/**
 * Applies superseding condition rules to assessment results.
 *
 * A rule marks its target condition as not assessed when the referenced
 * superseding condition exists in the same result set and reaches the rule's
 * minimum risk. The original severity snapshot is used for all rule evaluation
 * so one superseded condition cannot affect another rule in the same pass.
 */

module.exports = function supersedingCondition(assessments, supersedingConditions = []) {
  if (assessments.length === 0 || supersedingConditions.length === 0) {
    return { assessments, changed: false };
  }

  const assessmentByName = new Map();
  const originalSeverityByName = new Map();

  // Step 1: Index assessments and snapshot their original severity before any mutation.
  for (const assessment of assessments) {
    if (!assessment || !assessment.name) continue;

    assessmentByName.set(assessment.name, assessment);
    originalSeverityByName.set(assessment.name, assessment.severity);
  }

  const supersededByConditions = new Map();

  // Step 2: Evaluate every rule against the original severity snapshot.
  for (const rule of supersedingConditions) {
    const supersedingConditionName = rule.superseding_condition;
    const supersedingSeverity = originalSeverityByName.get(supersedingConditionName);

    if (!supersedingConditionName || !supersedingSeverity) {
      continue;
    }

    // Step 3: Mark the target condition only when the superseding condition meets the risk floor.
    const targetConditionName = rule.condition;
    const targetSeverity = originalSeverityByName.get(targetConditionName);
    if (!targetConditionName || !targetSeverity) {
      continue;
    }
    if (!matchesTriggerSeverity(supersedingSeverity, rule)) {
      continue;
    }
    if (!matchesTargetSeverity(targetSeverity, rule)) {
      continue;
    }

    // Default to hiding the display unless the rule explicitly requests not assessed.
    const action = rule.action === ACTIONS.NOT_ASSESSED ? ACTIONS.NOT_ASSESSED : ACTIONS.DISPLAY_HIDDEN;

    const displayHiddenReason = getDisplayHiddenReason(rule);

    const existing = supersededByConditions.get(targetConditionName);
    if (existing?.action !== ACTIONS.NOT_ASSESSED) {
      supersededByConditions.set(targetConditionName, {
        action, displayHiddenReason
      });
    }
  }

  if (supersededByConditions.size === 0) {
    return { assessments, changed: false };
  }

  let changed = false;

  // Step 4: Apply the superseding result only to matching assessment records.
  for (const [conditionName, supersedingAction] of supersededByConditions.entries()) {
    const assessment = assessmentByName.get(conditionName);

    if (!assessment) {
      continue;
    }

    if (supersedingAction.action === ACTIONS.NOT_ASSESSED) {
      assessment.severity = severities.not_assessed.name;
      assessment.severity_snomed = severities.not_assessed.name;
      assessment.triage_recommendation = triageEnum.none.name;
      assessment.display_hidden = undefined;

      assessment.display_hidden_reason = undefined;

      assessment.score = -1;
    } else {
      assessment.display_hidden = true;
      assessment.selected = false;
      assessment.display_hidden_reason = supersedingAction.displayHiddenReason;
    }
    changed = true;
  }

  return { assessments, changed };
}

/**
 * Checks whether a condition's severity meets the minimum severity required by
 * a superseding rule.
 *
 * Severity values are compared through the configured severity enum instead of
 * string ordering, so the rule remains correct when risk names are not lexical.
 */
function overMinRisk(actualRisk, minimumRisk) {
  const actualSeverity = severities[actualRisk];
  const minimumSeverity = severities[minimumRisk];

  if (!actualSeverity || !minimumSeverity) {
    return false;
  }

  return actualSeverity.value >= minimumSeverity.value;
}

// Checks the severity of superseding (trigger) condition
function matchesTriggerSeverity(actualSeverity, rule) {
  if (rule.superseding_min_severity) {
    return overMinRisk(actualSeverity, rule.superseding_min_severity);
  }

  return overMinRisk(actualSeverity, rule.risk);
}

// Checks the severity of the target conditions to suppress.
function matchesTargetSeverity(targetSeverity, rule) {
  if (rule.condition_min_severity) {
    return overMinRisk(targetSeverity, rule.condition_min_severity);
  }

  return true;
}

// Return id or generate a rule name for the assessment to be hidden.
function getDisplayHiddenReason(rule) {
  if (rule.id) {
    return rule.id;
  }

  return `csv_superseding:${rule.condition || 'unknown'}:${rule.superseding_condition || 'unknown'}:${rule.risk || 'unknown'}`;
}